/**
 * ChallengeWriter Component
 *
 * The writing editor for challenge submissions.
 * Features: auto-save, word count, submit button.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { Save, Send, Loader2, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { getWordCount } from '@/lib/textUtils'
import { checkContentSafety, getContentWarningExplanation } from '@/lib/contentSafety'
import { ContentWarning } from '@/components/ContentWarning'
import type { StoryChallenge, ChallengeSubmission } from '@/types/database'

// Autosave debounce time in ms
const AUTOSAVE_DELAY = 1500

interface ChallengeWriterProps {
  challenge: StoryChallenge
  submission: ChallengeSubmission
  onSave: (content: string) => Promise<void>
  onSubmit: () => Promise<void>
  isSubmitting?: boolean
  className?: string
}

export function ChallengeWriter({
  challenge,
  submission,
  onSave,
  onSubmit,
  isSubmitting = false,
  className,
}: ChallengeWriterProps) {
  const [content, setContent] = useState(submission.content || '')
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [showContentWarning, setShowContentWarning] = useState(false)
  const [contentWarningMessage, setContentWarningMessage] = useState<string | null>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const wordCount = getWordCount(content)
  const isWithinWordRange =
    wordCount >= challenge.word_goal_min && wordCount <= challenge.word_goal_max
  const canSubmit = wordCount >= challenge.word_goal_min && !isSaving && !isSubmitting

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.max(300, textareaRef.current.scrollHeight)}px`
    }
  }, [content])

  // Autosave with debounce
  const handleChange = useCallback(
    (newContent: string) => {
      setContent(newContent)

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      // Set new timeout for autosave
      saveTimeoutRef.current = setTimeout(async () => {
        setIsSaving(true)
        try {
          await onSave(newContent)
          setLastSaved(new Date())
        } catch (error) {
          console.error('Autosave failed:', error)
        } finally {
          setIsSaving(false)
        }
      }, AUTOSAVE_DELAY)
    },
    [onSave]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  // Manual save
  const handleManualSave = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    setIsSaving(true)
    try {
      await onSave(content)
      setLastSaved(new Date())
    } catch (error) {
      console.error('Save failed:', error)
    } finally {
      setIsSaving(false)
    }
  }

  // Handle submit with content safety check
  const handleSubmitClick = () => {
    const safetyResult = checkContentSafety(content)

    if (!safetyResult.isClean) {
      setContentWarningMessage(safetyResult.message)
      setShowContentWarning(true)
      return
    }

    onSubmit()
  }

  // Handle warning dialog actions
  const handleWarningReview = () => {
    setShowContentWarning(false)
    // Focus the textarea so they can edit
    textareaRef.current?.focus()
  }

  const handleWarningProceed = () => {
    setShowContentWarning(false)
    onSubmit()
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Prompt Card */}
      <div className="bg-[var(--color-kids-primary)]/10 rounded-[var(--radius-kid-lg)] p-4 border border-[var(--color-kids-primary)]/20">
        <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-2">
          Your Challenge
        </h3>
        <p className="text-[var(--color-kids-text)]">{challenge.prompt}</p>
        {challenge.example_opening && (
          <div className="mt-3 pt-3 border-t border-[var(--color-kids-primary)]/20">
            <p className="text-sm text-[var(--color-kids-text-muted)] italic">
              Example opening: "{challenge.example_opening}"
            </p>
          </div>
        )}
      </div>

      {/* Editor */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={e => handleChange(e.target.value)}
          placeholder="Start writing your story here..."
          disabled={isSubmitting}
          className={cn(
            'w-full min-h-[300px] p-4 rounded-[var(--radius-kid-lg)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-primary)]/20',
            'text-[var(--color-kids-text)] text-[var(--font-size-kid-base)]',
            'font-body leading-relaxed',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-primary)] focus:ring-2 focus:ring-[var(--color-kids-primary)]/20',
            'resize-none transition-all',
            isSubmitting && 'opacity-50 cursor-not-allowed'
          )}
        />

        {/* Saving indicator */}
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          {isSaving && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-1 text-xs text-[var(--color-kids-text-muted)]"
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </motion.span>
          )}
          {!isSaving && lastSaved && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-1 text-xs text-[var(--color-kids-success)]"
            >
              <CheckCircle className="w-3 h-3" />
              Saved
            </motion.span>
          )}
        </div>
      </div>

      {/* Footer: Word count + Actions */}
      <div className="flex items-center justify-between">
        {/* Word count */}
        <div className="flex items-center gap-3">
          <WordCountBadge
            current={wordCount}
            min={challenge.word_goal_min}
            max={challenge.word_goal_max}
          />
          {!isWithinWordRange && wordCount > 0 && (
            <span className="text-sm text-[var(--color-kids-text-muted)]">
              {wordCount < challenge.word_goal_min
                ? `${challenge.word_goal_min - wordCount} more words needed`
                : `${wordCount - challenge.word_goal_max} words over limit`}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleManualSave}
            disabled={isSaving || isSubmitting}
          >
            <Save className="w-4 h-4 mr-1" />
            Save
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmitClick}
            disabled={!canSubmit}
            isLoading={isSubmitting}
          >
            <Send className="w-4 h-4 mr-1" />
            Submit for Feedback
          </Button>
        </div>
      </div>

      {/* Content safety warning dialog */}
      {showContentWarning && contentWarningMessage && (
        <ContentWarning
          message={contentWarningMessage}
          explanation={getContentWarningExplanation()}
          onReview={handleWarningReview}
          onProceed={handleWarningProceed}
          isLoading={isSubmitting}
        />
      )}
    </div>
  )
}

interface WordCountBadgeProps {
  current: number
  min: number
  max: number
}

function WordCountBadge({ current, min, max }: WordCountBadgeProps) {
  const percentage = Math.min(100, (current / min) * 100)
  const isComplete = current >= min
  const isOver = current > max

  return (
    <div className="flex items-center gap-2">
      {/* Progress bar */}
      <div className="w-24 h-2 bg-[var(--color-kids-surface-alt)] rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          className={cn(
            'h-full rounded-full transition-colors',
            isOver
              ? 'bg-[var(--color-kids-warning)]'
              : isComplete
                ? 'bg-[var(--color-kids-success)]'
                : 'bg-[var(--color-kids-primary)]'
          )}
        />
      </div>

      {/* Word count text */}
      <span
        className={cn(
          'text-sm font-medium',
          isOver
            ? 'text-[var(--color-kids-warning)]'
            : isComplete
              ? 'text-[var(--color-kids-success)]'
              : 'text-[var(--color-kids-text-muted)]'
        )}
      >
        {current} / {min}–{max} words
      </span>
    </div>
  )
}

export default ChallengeWriter
